import type React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@/lib/theme";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthGuard } from "./features/auth/AuthGuard";
import { AppShell } from "./components/AppShell";
import { ClientsPage } from "./features/clients/ClientsPage";
import { ClientDetailPage } from "./features/clients/ClientDetailPage";
import { OverviewPage } from "./features/overview/OverviewPage";
import { IndustryInsightsPage } from "./features/insights/IndustryInsightsPage";
import { ComparisonPage } from "./features/compare/ComparisonPage";
import { CompareLandingPage } from "./features/compare/CompareLandingPage";
import { PresentPage } from "./features/present/PresentPage";
import { SurveyPage } from "./features/survey/SurveyPage";
import { SurveyResultsPage } from "./features/survey/SurveyResultsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function NotFound(): React.JSX.Element {
  return (
    <div className="rounded-lg border border-dashed p-12 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">Page not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        That page doesn't exist. Check the URL, or{" "}
        <Link to="/" className="font-medium text-phoenix-orange hover:underline">
          go to Clients
        </Link>
        .
      </p>
    </div>
  );
}

export function App(): React.JSX.Element {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Public survey form — outside AuthGuard, no login required */}
        <Routes>
          <Route
            path="/survey/:token"
            element={
              <ErrorBoundary>
                <SurveyPage />
              </ErrorBoundary>
            }
          />
          <Route
            path="*"
            element={
              <AuthGuard>
                <Routes>
                  <Route
                    path="/clients/:slug/present"
                    element={
                      <ErrorBoundary>
                        <PresentPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <ErrorBoundary>
                        <AppShell>
                          <Routes>
                            <Route
                              path="/"
                              element={
                                <ErrorBoundary>
                                  <ClientsPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/clients/:slug"
                              element={
                                <ErrorBoundary>
                                  <ClientDetailPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/overview"
                              element={
                                <ErrorBoundary>
                                  <OverviewPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/industry"
                              element={
                                <ErrorBoundary>
                                  <IndustryInsightsPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/compare"
                              element={
                                <ErrorBoundary>
                                  <CompareLandingPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/compare/:slugA/:slugB"
                              element={
                                <ErrorBoundary>
                                  <ComparisonPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route
                              path="/survey-results"
                              element={
                                <ErrorBoundary>
                                  <SurveyResultsPage />
                                </ErrorBoundary>
                              }
                            />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </AppShell>
                      </ErrorBoundary>
                    }
                  />
                </Routes>
              </AuthGuard>
            }
          />
        </Routes>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
    </ThemeProvider>
  );
}
